import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher } from '../useFetch';
import {
  IClientByEmailMatch,
  ICommunicationItem,
  IIncomingEmailRequest,
} from '../../Interfaces/ICommunication';

const KEY = 'communication';

export const useCommunicationByLegalClient = (idLegalClient: number | null) => {
  const enabled = idLegalClient !== null && idLegalClient > 0;
  const { data, isLoading, refetch } = useQuery<ICommunicationItem[]>({
    refetchOnWindowFocus: false,
    enabled,
    queryKey: [KEY, 'legalclient', idLegalClient],
    queryFn: () =>
      customFetcher({
        endpoint: `/communication/legalclient/${idLegalClient}`,
      }),
  });
  return {
    items: data ?? [],
    isLoading,
    refetch,
  };
};

export const useCommunicationByLegalTransaction = (
  idLegalTransaction: number | null
) => {
  const enabled = idLegalTransaction !== null && idLegalTransaction > 0;
  const { data, isLoading, refetch } = useQuery<ICommunicationItem[]>({
    refetchOnWindowFocus: false,
    enabled,
    queryKey: [KEY, 'legaltransaction', idLegalTransaction],
    queryFn: () =>
      customFetcher({
        endpoint: `/communication/legaltransaction/${idLegalTransaction}`,
      }),
  });
  return {
    items: data ?? [],
    isLoading,
    refetch,
  };
};

export const useRecordIncomingEmail = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (body: IIncomingEmailRequest) =>
      customFetcher({
        endpoint: '/communication/incoming',
        method: 'POST',
        body,
      }) as Promise<ICommunicationItem>,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
  return { recordIncoming: mutateAsync, isRecordingIncoming: isPending };
};

export const useDeleteCommunicationItem = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (itemId: string) =>
      customFetcher({
        endpoint: `/communication/${encodeURIComponent(itemId)}`,
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
  return {
    deleteCommunicationItem: mutateAsync,
    isDeletingCommunicationItem: isPending,
  };
};

export const useReplyToIncoming = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ idIncomingEmail, subject, body }: { idIncomingEmail: number; subject?: string; body: string }) =>
      customFetcher({
        endpoint: `/communication/incoming/${idIncomingEmail}/reply`,
        method: 'POST',
        body: { Subject: subject, Body: body },
      }) as Promise<ICommunicationItem>,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
  return { replyToIncoming: mutateAsync, isReplying: isPending };
};

export const useFindClientsByEmail = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (address: string) =>
      customFetcher({
        endpoint: `/communication/clients/by-email?address=${encodeURIComponent(
          address
        )}`,
      }) as Promise<IClientByEmailMatch[]>,
  });
  return { findByEmail: mutateAsync, isSearchingByEmail: isPending };
};
